import { useQuery } from '@apollo/client';
import { createContext, useState } from 'react';
import { downloadXlsx } from '../../../../components/Downloads/helpers';
import GraphControls from '../../../../components/Graphs/GraphControls';
import { GOOGLE_ANALYTICS_TAG } from '../../constants';
import { GRAPH_ASSETS_QUERY_NEW } from '../operations';
import {
  ASSET_AGGREGATE,
  COMPANY_AGGREGATE,
  defaultAggregateTypes,
  graphTitles,
  REMAINING_RESERVES,
  treeGraphTypeChildren,
  treeMapTypes,
} from '../utils';

const TreemapContext = createContext();

export const TreemapProvider = ({ children, portfolios }) => {
  const [openControlsModal, setOpenControlsModal] = useState(false);

  const [aggregate, setAggregate] = useState({
    'Layer One': COMPANY_AGGREGATE.name,
    'Layer Two': ASSET_AGGREGATE.name,
    'Layer Three': '',
  });
  const [graphType, setGraphType] = useState(REMAINING_RESERVES);
  const [graphTypeOptions, setGraphTypeOptions] = useState([]);
  const [zoomedNode, setZoomedNode] = useState('');

  const getFilteredGraphTypeOptions = () => {
    return (
      treeGraphTypeChildren[graphType.value]
        ?.filter(option => graphTypeOptions.some(selected => selected.name === option.name))
        .map(option => option.name) || []
    );
  };

  // TODO: permanently replace old queries with new ones
  const { data, loading } = useQuery(GRAPH_ASSETS_QUERY_NEW, {
    variables: {
      aggregates: Object.values(aggregate).filter(a => a !== ''),
      chartType: 'treemap',
      graphType: graphType.value,
      graphTypeOptions: getFilteredGraphTypeOptions(),
      portfolios,
    },
  });

  const downloadCallback = (errorHandler, successHandler) => {
    const path = 'portfolio-analysis/export-asset-treemap-data/';
    const body = {
      graphType: graphType.value,
      graphTypeOptions: getFilteredGraphTypeOptions(),
      portfolios,
    };
    const downloadName = `${portfolios[0].name}_Asset_${graphType.label}_Data.xlsx`;

    downloadXlsx(path, body, downloadName, errorHandler, successHandler);
  };

  const onClick = ({ points }) => {
    const node = points[0];
    if (node && node.id) {
      setZoomedNode(node.id);
    } else {
      setZoomedNode('');
    }
  };

  const title = `${graphType.label} ${graphTitles[graphType.value]}`;

  return (
    <TreemapContext.Provider
      value={{
        title,
        setOpenControlsModal,
        labels: data?.graphAssetsNew?.analytics[0].labels || [],
        parents: data?.graphAssetsNew?.analytics[0].parents || [],
        values: data?.graphAssetsNew?.analytics[0].values || [],
        customdata: data?.graphAssetsNew?.analytics[0].customdata || [],
        ids: data?.graphAssetsNew?.analytics[0].ids || [],
        hovertemplate: data?.graphAssetsNew?.analytics[0].hovertemplate || '',
        texttemplate: data?.graphAssetsNew?.analytics[0].texttemplate || [],
        loading,
        downloadCallback,
        onClick,
        zoomedNode,
        googleAnalyticsTag: GOOGLE_ANALYTICS_TAG,
      }}
    >
      {children}
      <GraphControls
        aggregate={aggregate}
        aggregateTypes={defaultAggregateTypes}
        aggregateLayers={Object.keys(aggregate)}
        googleAnalyticsTag={GOOGLE_ANALYTICS_TAG}
        graphType={graphType}
        graphTypeChildren={treeGraphTypeChildren}
        graphTypeOptions={graphTypeOptions}
        includeOptions
        open={openControlsModal}
        setAggregate={setAggregate}
        setGraphType={setGraphType}
        setGraphTypeOptions={setGraphTypeOptions}
        setOpen={setOpenControlsModal}
        types={treeMapTypes}
      />
    </TreemapContext.Provider>
  );
};

export const TreemapConsumer = TreemapContext.Consumer;

export default TreemapContext;
